import Phaser from 'phaser';
import {
  NAME_ADJECTIVES,
  NAME_NOUNS,
  nameText,
  randomName,
  stepName,
  type WordName,
} from '../../content/names';
import { haptics } from '../../platform/haptics';
import { ensurePlayer, onlineOn, renamePlayer } from '../online';
import { saves } from '../records';
import { bevelButton, clearScene, navyPanel } from '../ui/chrome';
import { openTab } from '../ui/navChrome';
import { label, UI } from '../ui/text';

type Status = 'joining' | 'offline' | 'saving' | 'saved' | 'refused';

const STATUS_TEXT: Record<Status, { text: string; color: number }> = {
  joining: { text: 'Setting up...', color: UI.dim },
  offline: { text: "Can't reach the server right now. Try again later.", color: UI.dim },
  saving: { text: 'Saving...', color: UI.dim },
  saved: { text: 'Name saved.', color: UI.good },
  refused: { text: "That name can't be used. Pick another.", color: UI.danger },
};

const PARTS = ['WORD 1', 'WORD 2', 'NUMBER'] as const;

/**
 * CLUB > YOU > RENAME (docs/ONLINE.md, "Players and names"): the player's online name, two words
 * from the lists and a number, changed with arrows or SHUFFLE and saved to the server. Joins first
 * when online scores are on and there is no player yet.
 */
export class NameScene extends Phaser.Scene {
  /** The name being picked (not yet saved), or null to show the saved one. */
  private draft: WordName | null = null;
  private status: Status | null = null;
  private busy = false;

  constructor() {
    super('Name');
  }

  init(): void {
    this.draft = null;
    this.status = null;
    this.busy = false;
  }

  create(): void {
    this.cameras.main.setBackgroundColor('#15102f');
    this.draw();
    void this.join();
  }

  private readonly redraw = (): void => {
    if (!this.scene.isActive()) return;
    clearScene(this);
    this.draw();
  };

  private async join(): Promise<void> {
    if (!onlineOn() || saves().save.online.player) return;
    this.status = 'joining';
    this.redraw();
    const player = await ensurePlayer();
    this.status = player ? null : 'offline';
    this.redraw();
  }

  private saved(): WordName | null {
    const name = saves().save.online.player?.name;
    return name?.kind === 'words' ? name : null;
  }

  private draw(): void {
    const { width, height } = this.scale.gameSize;
    const g = this.add.graphics();
    g.fillStyle(UI.panel).fillRect(0, 0, width, 22);
    g.fillStyle(UI.rule).fillRect(0, 22, width, 1);
    label(this, 6, 4, 'YOUR NAME', 2);
    let y = 30;
    label(this, width / 2, y, 'Shown on the leaderboards.', 1, UI.dim).setOrigin(0.5, 0);
    y += 16;

    const saved = this.saved();
    const name = this.draft ?? saved;
    if (onlineOn() && name) {
      navyPanel(g, 4, y, width - 8, 92);
      label(this, width / 2, y + 8, nameText(name), 1, UI.accent).setOrigin(0.5, 0);
      PARTS.forEach((_, part) => {
        const ry = y + 24 + part * 20;
        const p = part as 0 | 1 | 2;
        bevelButton(this, 12, ry, 22, 16, '<', () => this.step(p, -1));
        bevelButton(this, width - 34, ry, 22, 16, '>', () => this.step(p, 1));
        const text =
          p === 2
            ? String(name.number)
            : ((p === 0 ? NAME_ADJECTIVES : NAME_NOUNS)[name.parts[p]] ?? '-');
        label(this, width / 2, ry + 4, text).setOrigin(0.5, 0);
      });
      y += 100;
      const half = Math.floor((width - 16) / 2);
      const changed = !!this.draft && nameText(this.draft) !== (saved ? nameText(saved) : '');
      bevelButton(this, 6, y, half, 18, 'SHUFFLE', () => this.shuffle());
      bevelButton(this, 10 + half, y, half, 18, 'SAVE NAME', () => void this.save(), changed);
      y += 26;
    } else if (!onlineOn()) {
      label(this, width / 2, y, 'Online scores are off.', 1, UI.accent).setOrigin(0.5, 0);
      y += 14;
    }
    if (this.status) {
      const s = STATUS_TEXT[this.status];
      label(this, width / 2, y, s.text, 1, s.color)
        .setOrigin(0.5, 0)
        .setMaxWidth(width - 16)
        .setCenterAlign();
    }
    bevelButton(this, 6, height - 30, width - 12, 24, 'BACK', () => openTab(this, 'club'));
  }

  private step(part: 0 | 1 | 2, step: 1 | -1): void {
    const name = this.draft ?? this.saved();
    if (!name || this.busy) return;
    this.draft = stepName(name, part, step);
    this.status = null;
    this.redraw();
  }

  private shuffle(): void {
    if (this.busy) return;
    this.draft = randomName(Math.random(), Math.random(), Math.random());
    this.status = null;
    this.redraw();
  }

  private async save(): Promise<void> {
    if (!this.draft || this.busy) return;
    this.busy = true;
    this.status = 'saving';
    this.redraw();
    const outcome = await renamePlayer(this.draft);
    this.busy = false;
    if (outcome === 'ok') {
      this.draft = null;
      this.status = 'saved';
      haptics.tap();
    } else {
      this.status = outcome === 'refused' ? 'refused' : 'offline';
    }
    this.redraw();
  }
}
